import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, first, map, takeUntil } from 'rxjs/operators';

import { Chat, ChatType } from '../models/chat.models';
import { ChatService } from '../services/chat.service';
import {
    ChatComponentInitilized,
    ChatComponentDestroyed,
    CloseChat,
    ToggleChat,
    EnableMocha,
    EnableHubtype,
    InitHubtypeWidget,
    ShowSmallButtonChat,
    ShowLargeButtonChat,
} from '../store/chat.actions';
import {
    getHubtype,
    getKakaoTalk,
    getMochaChat,
    getStyleAdvisor,
    getUrlChat,
    selectActiveChat,
    selectChatOpened,
    selectChatVisible,
    selectIsLargeButtonChat,
    showChatButton,
} from '../store/chat.selectors';

import { Resolutions } from '@core/domain/state/core.state';
import {
    getCmsConfigValue,
    getLanguageisRTL,
    getPage,
    getResolution,
    getStoreAndLanguage,
} from '@core/infrastructure/state/core.selectors';
import {
    AccessibilityChatWidgetUiService,
} from '@layout/ui/services/accessibility-chat-widget-ui.service';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { AnalyticsEvent } from '@shared/analytics/store/analytics.actions';
import { WhatsappService } from '@shared/contact/whatsapp.service';

// TODO ECOMDUTI-21019 [TechDebt] [ECOMDUTI-17958]- Refactor de chat
@Component({
    providers: [AccessibilityChatWidgetUiService],
    selector: 'chat',
    templateUrl: './chat.component.html',
})
export class ChatComponent implements OnInit, OnDestroy {
    public useFloatingButton$ = this.store.select(getPage).pipe(map((page) => page !== 'ItxAngularContactPage'));

    public chatVisible$: Observable<boolean> = this.store.select(selectChatVisible);

    public activeChat$: Observable<ChatType> = this.store.select(selectActiveChat);

    public hubTypeChat$: Observable<Chat> = this.store.select(getHubtype);

    public urlChat$: Observable<Chat> = this.store.select(getUrlChat);

    public kakaoChat$: Observable<Chat> = this.store.select(getKakaoTalk);

    public mochaChat$: Observable<Chat> = this.store.select(getMochaChat);

    // TODO ECOMDUTI-21019 revisar si esto se utiliza o sirve para algo aquí en el chat
    public styleAdvisor$: Observable<Chat> = this.store.select(getStyleAdvisor);

    public chatOpened$: Observable<boolean> = this.store.select(selectChatOpened);

    private readonly onDestroy$ = new Subject<void>();

    public showLiveHelp$ = this.activeChat$.pipe(
        map(activeChat => activeChat === 'LIVE_HELP_WINDOW' || activeChat === 'KAKAOTALK')
    );

    public isRtl$: Observable<boolean> = this.store.select(getLanguageisRTL);

    public isLightTheme$ = this.accessibilityChatSharedService.isLightTheme$;

    public isButtonCenteredByPage$ = this.accessibilityChatSharedService.isButtonCenteredByPage$;

    public showChatButton$ = this.store.select(showChatButton);

    constructor(
        private readonly chatService: ChatService,
        private readonly whatsappService: WhatsappService,
        private readonly store: Store,
        private readonly accessibilityChatSharedService: AccessibilityChatWidgetUiService
    ) { }

    public liveHelpConfiguration = {} as any;

    public buttonStyles = {
        closed: true,
        open: false,
    };

    public isAnimated$ = this.store.select(selectIsLargeButtonChat);

    public isMobile = false;

    public closeSelection = false;

    public stopAnimation = false;

    // Funcional pasados 2 segundos se debe mostrar el 'need help' y pasado 8 quitarlo
    public startAnimationSecs = 2000;

    public stopAnimationSecs = 8000;

    public isWhatsApp$ = this.store.select(getCmsConfigValue('enable_digital_channels')).pipe(
        filter(enableDigitalChannels => Object.keys(enableDigitalChannels).length > 0),
        first(),
        map((enableDigitalChannels) => enableDigitalChannels.contact.find((contact: string) => contact === 'whatsapp')),
    );

    public whatsAppConfig$ = this.whatsappService.whatsAppInfo$;

    public enableDigitalChannels$ = combineLatest([
        this.store.select(getCmsConfigValue('enable_digital_channels')),
        this.store.select(getStoreAndLanguage),
        this.store.select(getPage),
    ]).pipe(
        map(([enableDigitalChannels, storeAndLanguage, currentPage]) => {
            let haveDigitalChannels = false;

            if (Object.keys(enableDigitalChannels).length > 0) {
                const isPage = enableDigitalChannels?.pages.find((page: string) => page === currentPage);
                const isLanguage = enableDigitalChannels?.languages.find(
                    (language) => language === +storeAndLanguage.languageId
                );
                if (isPage && isLanguage && enableDigitalChannels?.active) {
                    haveDigitalChannels = true;
                }
            }

            return haveDigitalChannels;
        }),
        takeUntil(this.onDestroy$),
    );

    public ngOnInit(): void {
        this.store.select(showChatButton).pipe(
            filter(val => val),
            takeUntil(this.onDestroy$)
        ).subscribe((_) => {
            this.initChatBot();
        });
    }

    public openChat(): void {
        this.store.dispatch(ToggleChat());
    }

    public openSelection(): void {
        this.store.dispatch(ShowSmallButtonChat());
        this.stopAnimation = true;
        this.enableDigitalChannels$.pipe(
            first(),
        ).subscribe((enableDigitalChannels) => {
            if (enableDigitalChannels) {
                this.closeSelection = !this.closeSelection;
            } else {
                this.openChat();
            }
        });
    }

    public showHideButton(status: string): void {
        if (status === 'hide') {
            this.buttonStyles = {
                closed: true,
                open: false,
            };
        } else if (status === 'show') {
            this.buttonStyles = {
                closed: false,
                open: true,
            };
        }
    }

    /**
     * Función para aplicar las animaciones al botón flotante
     */
    public animateButton(): void {
        // Añade la animación
        setTimeout(() => {
            if (!this.stopAnimation && !localStorage.getItem('hubtypeInUse')) {
                this.store.dispatch(ShowLargeButtonChat());
            }
        }, this.startAnimationSecs);
        // Termina la animación
        setTimeout(() => {
            this.store.dispatch(ShowSmallButtonChat());
        }, this.stopAnimationSecs);
    }

    public closeLiveHelpWindow(): void {
        this.store.dispatch(CloseChat());
    }

    /**
     * Función para abrir Hubtype
     */
    public openHubtype(chat: Chat): void {
        this.liveHelpConfiguration.showLiveHelpWindow = false;
        this.store.dispatch(EnableHubtype({ payload: chat.configuration }));
        this.store.dispatch(InitHubtypeWidget({ configuration: chat.configuration }));
    }

    /**
     * Función para abrir Url Chat
     */
    public openUrlChat(chat: Chat): void {
        this.liveHelpConfiguration.showLiveHelpWindow = false;
        this.chatService.openUrlChat(chat);
    }

    public openMochaChat(): void {
        this.liveHelpConfiguration.showLiveHelpWindow = false;
        this.store.dispatch(EnableMocha({}));
    }

    public ngOnDestroy(): void {
        this.store.dispatch(ChatComponentDestroyed());
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    private initChatBot(): void {
        this.store.dispatch(ChatComponentInitilized());
        this.chatOpened$.pipe(takeUntil(this.onDestroy$)).subscribe((opened) => {
            if (opened) {
                this.store.dispatch(AnalyticsEvent({
                    eventData: {
                        cf: 'ayuda',
                        category: 'envio',
                        action: 'abrir_chat',
                        label: '',
                        eventFrom: EventFrom.chat,
                    },
                }));
                this.showHideButton('hide');
                this.closeSelection = false;
            } else {
                this.showHideButton('show');
            }
        });
        this.store.select(getResolution).pipe(first()).subscribe((resolution) => {
            if (resolution !== Resolutions.TABLET && resolution !== Resolutions.MOBILE) {
                this.animateButton();
            }
            this.isMobile = resolution === Resolutions.MOBILE;
        });
    }
}
